import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { completeCheckoutSession } from '../api/caApi'
import { useCart } from '../state/CartContext'
import { useI18n } from '../state/I18nContext'

const CheckoutSuccess: React.FC = () => {
  const { clear } = useCart()
  const { t, locale } = useI18n()
  const location = useLocation()
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [orderId, setOrderId] = React.useState<number | null>(null)
  const inflightRef = React.useRef<string | null>(null)
  const doneRef = React.useRef<Set<string>>(new Set())
  const clearRef = React.useRef(clear)
  const tRef = React.useRef(t)

  React.useEffect(() => {
    clearRef.current = clear
  }, [clear])

  React.useEffect(() => {
    tRef.current = t
  }, [t])

  const sessionId = React.useMemo(() => {
    const qs = new URLSearchParams(location.search)
    return qs.get('session_id') || ''
  }, [location.search])

  React.useEffect(() => {
    if (!sessionId) {
      setError(tRef.current.checkout.failed)
      setLoading(false)
      return
    }

    const key = `${sessionId}|${locale}`
    if (doneRef.current.has(key)) {
      setLoading(false)
      return
    }
    if (inflightRef.current === key) {
      return
    }
    inflightRef.current = key

    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const result = await completeCheckoutSession(sessionId, { lang: locale })
        if (!alive) return
        setOrderId(result.order_id)
        clearRef.current()
        doneRef.current.add(key)
        window.dispatchEvent(new CustomEvent('ca:purchase', {
          detail: {
            transaction_id: sessionId,
            value:          result.amounts.total,
            currency:       result.currency.toUpperCase(),
            items:          result.items.map(it => ({
              item_id:   it.item_id,
              item_name: it.item_name,
              price:     it.price,
              quantity:  it.quantity,
            })),
          },
        }))
      } catch (err: any) {
        if (!alive) return
        const code = String(err?.code || 'request_failed') as keyof typeof t.apiErrors
        const mapped = (t.apiErrors as any)[code] as string | undefined
        setError(mapped || err?.message || tRef.current.checkout.failed)
      } finally {
        if (!alive) return
        setLoading(false)
        if (inflightRef.current === key) inflightRef.current = null
      }
    })()

    return () => {
      alive = false
      if (inflightRef.current === key) inflightRef.current = null
    }
  }, [sessionId, locale])

  return (
    <section className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold">{t.checkout.title}</h1>

      {loading ? (
        <p className="mt-4 text-neutral-700">{t.checkout.placingOrder}</p>
      ) : null}

      {error ? (
        <p className="mt-4 text-red-700">{error}</p>
      ) : null}

      {!loading && !error && orderId !== null ? (
        <div className="mt-6 rounded-xl border border-neutral-200 p-6 bg-white">
          <p className="text-neutral-800">
            {t.checkout.thankYou.replace('{orderId}', String(orderId))}
          </p>
          <Link
            to={`/${locale}/taste`}
            className="inline-block mt-4 rounded-lg bg-amber-600 text-white px-5 py-3 font-semibold hover:bg-amber-700"
          >
            {t.cartPage.browseCatalog}
          </Link>
        </div>
      ) : null}
    </section>
  )
}

export default CheckoutSuccess
